import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { useTranslation } from 'react-i18next';
import '@fontsource-variable/inter';
import './styles.css';
import '@nexus/editor/editor.css';
import i18n from './i18n';
import { App } from './App';
import { openStore, type OpenedStore } from './db/open';
import { StoreProvider } from './db/context';
import { useUi } from './state/ui';
import { SignInGate } from './sync/SignInGate';
import { lockedBy, openAnyway } from './db/backup';
import { loadServerConfig } from './sync/config';
import { inviteInAddress } from './sync/connect';
import { ConnectPrompt } from './sync/ServerSettings';

/** Keeps <html data-theme/lang> in sync with the settings and the OS theme. */
function useThemeAndLanguage() {
  const theme = useUi((s) => s.theme);
  const language = useUi((s) => s.language);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const apply = () => {
      const dark = theme === 'dark' || (theme === 'system' && mq.matches);
      document.documentElement.dataset.theme = dark ? 'dark' : 'light';
      // The phone's status bar takes the app's own background (no dark bar above a light app).
      const bg = getComputedStyle(document.body).backgroundColor;
      document.querySelector('meta[name="theme-color"]')?.setAttribute('content', bg);
    };
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, [theme]);
  useEffect(() => {
    void i18n.changeLanguage(language);
    document.documentElement.lang = language;
  }, [language]);
}

// Read the server settings, then open once, outside React, so StrictMode's double
// effects never open two databases.
const opening = loadServerConfig().then(openStore);

function Root() {
  const { t } = useTranslation();
  const [state, setState] = useState<{ opened?: OpenedStore; error?: string }>({});
  const [invite, setInvite] = useState(inviteInAddress);
  useThemeAndLanguage();

  useEffect(() => {
    opening.then(
      (opened) => setState({ opened }),
      (e: unknown) => setState({ error: e instanceof Error ? e.message : String(e) }),
    );
  }, []);

  const host = state.error ? lockedBy(state.error) : null;
  if (host) {
    return (
      <div role="alert" className="max-w-xl p-10">
        <p className="font-semibold">{t('data.lockedTitle')}</p>
        <p className="mt-2 text-sm text-fg-muted">{t('data.lockedBody', { host })}</p>
        <button
          type="button"
          onClick={() => void openAnyway()}
          className="mt-4 rounded-md bg-danger px-3 py-1.5 text-sm font-medium text-white dark:text-black"
        >
          {t('data.openAnyway')}
        </button>
      </div>
    );
  }
  if (state.error) {
    return (
      <div role="alert" className="p-10">
        <p className="font-semibold">{t('app.loadError')}</p>
        <pre className="mt-2 text-sm whitespace-pre-wrap text-fg-muted">{state.error}</pre>
      </div>
    );
  }
  if (!state.opened) return <p className="p-10 text-fg-muted">{t('app.loading')}</p>;
  if (invite)
    return (
      <ConnectPrompt
        conn={invite}
        onSkip={() => {
          history.replaceState(null, '', window.location.pathname);
          setInvite(null);
        }}
      />
    );
  return (
    <SignInGate>
      <StoreProvider value={state.opened}>
        <App />
      </StoreProvider>
    </SignInGate>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Root />
  </StrictMode>,
);
